/**
 * InviteMedecinModal — opens an `invite` lien for a médecin (POST /referral/medecins/inviter). On
 * success the server returns the single-use seam-2 callback token; we surface it (copyable, mono) so the
 * operator can resolve the decision in the wiring/test flow. Gated by referral.lien.manage (the caller
 * only mounts the trigger when canManage; the submit is additionally disabled without it).
 *
 * Keyboard-first: native form, Enter submits, Esc closes (Modal handles the trap/restore).
 */
import { useEffect, useId, useState } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { useT } from "@/app";
import { useInviteMedecin, type InviteVars } from "../api";
import type { InviteResult } from "../types";
import { isValidEmail, parsePct } from "../lib";

export interface InviteMedecinModalProps {
  open: boolean;
  labId: string | null;
  disabled?: boolean;
  onClose: () => void;
  /** Receives the invite result so the caller can record médecin_id → lien_id (the read omits lien_id). */
  onInvited?: (result: InviteResult) => void;
}

export function InviteMedecinModal({
  open,
  labId,
  disabled,
  onClose,
  onInvited,
}: InviteMedecinModalProps) {
  const { t } = useT();
  const toast = useToast();
  const invite = useInviteMedecin(labId);
  const formId = useId();

  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [email, setEmail] = useState("");
  const [remise, setRemise] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Reset all local state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setNom("");
      setPrenom("");
      setSpecialite("");
      setEmail("");
      setRemise("");
      setToken(null);
      setTouched(false);
    }
  }, [open]);

  const pct = parsePct(remise);
  const nomError = touched && nom.trim() === "" ? t("referral.field.required") : undefined;
  const emailError =
    touched && !isValidEmail(email) ? t("referral.field.emailInvalid") : undefined;
  const remiseError = touched && pct === null ? t("referral.field.pctInvalid") : undefined;
  const canSubmit =
    !disabled && nom.trim() !== "" && isValidEmail(email) && pct !== null && !invite.isPending;

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    const vars: InviteVars = { nom: nom.trim(), novera_email: email.trim() };
    if (prenom.trim()) vars.prenom = prenom.trim();
    if (specialite.trim()) vars.specialite = specialite.trim();
    if (pct !== undefined) vars.remise_pct = pct;
    invite.mutate(vars, {
      onSuccess: (res) => {
        toast.success(t("referral.invite.success", { nom: nom.trim() }));
        setToken(res.callback_token);
        onInvited?.(res);
      },
      onError: (err) => toast.error(err.message, t("referral.invite.title")),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("referral.invite.title")}
      footer={
        token ? (
          <Button variant="primary" onClick={onClose}>
            {t("action.close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("referral.action.cancel")}
            </Button>
            <Button
              variant="primary"
              form={formId}
              type="submit"
              loading={invite.isPending}
              disabled={!canSubmit}
            >
              {t("referral.invite.submit")}
            </Button>
          </>
        )
      }
    >
      {token ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
          <div style={{ fontWeight: 600 }}>{t("referral.invite.token.title")}</div>
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("referral.invite.token.desc")}</p>
          <code
            className="nv-mono nv-ltr-island"
            style={{
              display: "block",
              padding: "var(--nv-s-2)",
              background: "var(--nv-surface-2)",
              border: "1px solid var(--nv-line-faint)",
              borderRadius: "var(--nv-r-sm)",
              wordBreak: "break-all",
            }}
          >
            {token}
          </code>
        </div>
      ) : (
        <form
          id={formId}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
        >
          <FormField label={t("referral.invite.nom")} required error={nomError}>
            {(p) => (
              <Input
                {...p}
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                autoFocus
                invalid={!!nomError}
              />
            )}
          </FormField>
          <FormField label={t("referral.invite.prenom")}>
            {(p) => <Input {...p} value={prenom} onChange={(e) => setPrenom(e.target.value)} />}
          </FormField>
          <FormField label={t("referral.invite.specialite")}>
            {(p) => (
              <Input {...p} value={specialite} onChange={(e) => setSpecialite(e.target.value)} />
            )}
          </FormField>
          <FormField
            label={t("referral.invite.email")}
            required
            hint={t("referral.invite.email.hint")}
            error={emailError}
          >
            {(p) => (
              <Input
                {...p}
                type="email"
                inputMode="email"
                className="nv-ltr-island"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!emailError}
              />
            )}
          </FormField>
          <FormField
            label={t("referral.invite.remise")}
            hint={t("referral.invite.remise.hint")}
            error={remiseError}
          >
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="decimal"
                value={remise}
                onChange={(e) => setRemise(e.target.value)}
                invalid={!!remiseError}
              />
            )}
          </FormField>
        </form>
      )}
    </Modal>
  );
}
